/**
 * Subscription Status Types and Constants
 *
 * This module provides type-safe constants for subscription statuses used throughout the application.
 * It distinguishes between Stripe's native subscription statuses and our custom statuses.
 *
 * @example
 * ```typescript
 * // Check if a subscription is active
 * if (isActiveSubscription(subscription.status)) {
 *   // Handle active subscription
 * }
 *
 * // Compare against specific status
 * if (subscriptionStatus.status === SUBSCRIPTION_STATUS.FREE_TIER) {
 *   // Handle free tier
 * }
 * ```
 */

// Stripe subscription statuses
export const STRIPE_SUBSCRIPTION_STATUS = {
  ACTIVE: 'active',
  TRIALING: 'trialing',
  INCOMPLETE: 'incomplete',
  PAST_DUE: 'past_due',
  UNPAID: 'unpaid',
  CANCELED: 'canceled',
  INCOMPLETE_EXPIRED: 'incomplete_expired',
  PAUSED: 'paused',
} as const;

// Custom subscription statuses (added by our app)
export const CUSTOM_SUBSCRIPTION_STATUS = {
  FREE_TIER: 'free_tier',
  OPEN_SOURCE: 'open_source',
} as const;

// All possible subscription statuses
export const SUBSCRIPTION_STATUS = {
  ...STRIPE_SUBSCRIPTION_STATUS,
  ...CUSTOM_SUBSCRIPTION_STATUS,
} as const;

// Type for Stripe subscription statuses
export type StripeSubscriptionStatus =
  (typeof STRIPE_SUBSCRIPTION_STATUS)[keyof typeof STRIPE_SUBSCRIPTION_STATUS];

// Type for custom subscription statuses
export type CustomSubscriptionStatus =
  (typeof CUSTOM_SUBSCRIPTION_STATUS)[keyof typeof CUSTOM_SUBSCRIPTION_STATUS];

// Type for all subscription statuses
export type SubscriptionStatusType = (typeof SUBSCRIPTION_STATUS)[keyof typeof SUBSCRIPTION_STATUS];

// Helper functions for subscription status checks
export const isActiveOrTrialingSubscription = (status: string): boolean => {
  return (
    [STRIPE_SUBSCRIPTION_STATUS.ACTIVE, STRIPE_SUBSCRIPTION_STATUS.TRIALING] as string[]
  ).includes(status);
};

export const needsAttentionSubscription = (status: string): boolean => {
  return (
    [
      STRIPE_SUBSCRIPTION_STATUS.INCOMPLETE,
      STRIPE_SUBSCRIPTION_STATUS.PAST_DUE,
      STRIPE_SUBSCRIPTION_STATUS.UNPAID,
    ] as string[]
  ).includes(status);
};

export const isCanceledSubscription = (status: string): boolean => {
  return (
    [STRIPE_SUBSCRIPTION_STATUS.CANCELED, STRIPE_SUBSCRIPTION_STATUS.INCOMPLETE_EXPIRED] as string[]
  ).includes(status);
};
